/**
 * "REVIEW DAILY REPORT" — the confirmation that did not exist (WP-41 / R-23).
 *
 * The control was a plain button wired straight to the send: `onClick` →
 * `createItem('daily-report', …)` → the route renders two PDFs and mails them,
 * in-request. No confirmation, and no user-facing copy anywhere naming a single
 * recipient — not on the button, not in a tooltip, not in the toast.
 *
 * What it actually does is email EVERYONE SENIOR TO THE SENDER who covers that
 * building, plus every owner in the organisation, plus the building's own
 * contact addresses. A concierge had no way to discover from the interface that
 * pressing it escalates to management. The entire explanation existed only as a
 * comment in `reports.js`.
 *
 * THE LIST IS RESOLVED SERVER-SIDE AND IS NOT EDITABLE (R-23). It is fetched
 * from the same function that does the mailing, so the dialog cannot promise one
 * audience while the server mails another. It is read-only on purpose: an
 * editable list would let a concierge quietly drop their manager, which is the
 * one thing the escalation ladder exists to prevent.
 *
 * STAFF ARE NAMED, NOT ADDRESSED. The server returns names and roles for people
 * and full addresses only for the building's own contact mailbox — a concierge
 * holds no `user:read`, and this button must not become a staff directory.
 *
 * IF THE LIST CANNOT BE RESOLVED, THE SEND IS NOT OFFERED. A confirmation that
 * cannot say who is not a confirmation.
 */
import React, { useCallback, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { fetchAllItems } from '../../../../requests/requests';
import { FormModal } from '../../../../modal/form-modal';
import { AppButton } from '../../../../button/app-button';
import { formatDateOnly, zoneNote } from '../../../../../function-utils/building-time';
import {
  palette,
  spacing,
  radius,
  status,
  typography,
} from '../../../../../style-utils/theme';

export interface RecipientPreview {
  building: { id: number; name: string };
  date: string;
  timezone: string;
  /** The BUILDING's clock, whoever asks (a report that is SENT is on the
   *  building's clock, zone named — R-T12), and the label to print for it:
   *  "Toronto time (UTC-04:00)". */
  tz?: string;
  tz_source?: 'viewer' | 'building' | 'fallback';
  timezone_label?: string;
  attachments: string[];
  contacts: string[];
  escalation: { id: string; name: string | null; role: string }[];
  /** Plan 3 F4: nobody else would receive it, so it goes to the sender. */
  to_sender?: boolean;
  sender?: { name: string | null; email: string } | null;
  total: number;
}

export const TO_SENDER_NOTE =
  'It will be sent to you — add a building contact to send it to others.';

/** The stored value is a capability key (`property_manager`), never a label. */
const ROLE_LABEL: Record<string, string> = {
  owner: 'Owner',
  property_manager: 'Property manager',
  concierge: 'Concierge',
};

const roleLabel = (role: string): string => ROLE_LABEL[role] || role;

/* The report's day is a KEY on the building's clock: printed as it is, never
   through a Date (which would read it as a UTC instant). */
const reportDay = (preview: RecipientPreview): string =>
  formatDateOnly(preview.date) || preview.date;

/* The server's own label, verbatim; one is derived only for an older answer. */
const reportZoneLabel = (preview: RecipientPreview): string =>
  preview.timezone_label || zoneNote(preview.tz || preview.timezone) || preview.timezone;

export interface DailyReportConfirmProps {
  buildingId: number;
  sending: boolean;
  onConfirm: () => void;
  onClose: () => void;
}

export const DailyReportConfirm: React.FC<DailyReportConfirmProps> = ({
  buildingId,
  sending,
  onConfirm,
  onClose,
}) => {
  const [preview, setPreview] = useState<RecipientPreview | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = (await fetchAllItems('daily-report/recipients', undefined, {
        property_id: buildingId,
      })) as unknown as RecipientPreview;
      setPreview(data);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      setPreview(null);
      setError(
        err?.response?.data?.message ||
          'We could not work out who this report would go to.'
      );
    } finally {
      setLoading(false);
    }
  }, [buildingId]);

  useEffect(() => {
    void load();
  }, [load]);

  const nobody = preview !== null && preview.total === 0;
  const toSender = !!preview?.to_sender && !!preview.sender;
  // Not offered while the audience is unknown, and not offered when there is no
  // audience: a send that reaches nobody renders two PDFs and mails them into
  // the void. This is the same rule WP-29 applied to destructive rows — a
  // control that cannot achieve anything says so instead of failing after the
  // click.
  const canSend = Boolean(preview) && !loading && !error && !nobody;

  return (
    <FormModal
      title="Review daily report"
      subtitle={
        preview
          ? `${preview.building.name} · desk day ${reportDay(preview)}, ${reportZoneLabel(preview)}`
          : 'Working out who this would go to…'
      }
      onClose={onClose}
      footer={
        <>
          <AppButton variant="secondary" onClick={onClose}>
            Cancel
          </AppButton>
          <AppButton
            onClick={onConfirm}
            disabled={!canSend}
            loading={sending}
            loadingLabel="Sending daily report"
            aria-label={
              canSend && preview
                ? `Send this report to ${preview.total} recipient${
                    preview.total === 1 ? '' : 's'
                  }`
                : 'Send report'
            }
          >
            Send report
          </AppButton>
        </>
      }
    >
      {loading ? (
        <Muted>Resolving recipients…</Muted>
      ) : error ? (
        <Alert role="alert">
          {error}{' '}
          <RetryButton type="button" onClick={load}>
            Try again
          </RetryButton>
        </Alert>
      ) : preview ? (
        <>
          <Lead>
            This sends the day&apos;s report for{' '}
            <strong>{preview.building.name}</strong> covering the desk day of{' '}
            <strong>{reportDay(preview)}</strong>, with every time shown in{' '}
            <strong data-report-zone="">{reportZoneLabel(preview)}</strong> — the
            building&apos;s own clock, whoever sends it — as{' '}
            {preview.attachments.length} PDF attachment
            {preview.attachments.length === 1 ? '' : 's'}:{' '}
            {preview.attachments.join(' and ')}.
          </Lead>

          <SectionTitle>
            {nobody
              ? 'It would reach nobody'
              : `It goes to ${preview.total} recipient${
                  preview.total === 1 ? '' : 's'
                }`}
          </SectionTitle>

          {nobody ? (
            <Alert role="alert">
              Nobody senior to you covers this building, and it has no contact
              address on file, so this report has no audience. Add a contact
              address to the building first.
            </Alert>
          ) : (
            <List>
              {preview.escalation.map(person => (
                <Row key={person.id}>
                  <Name>{person.name || 'A colleague'}</Name>
                  <RolePill>{roleLabel(person.role)}</RolePill>
                </Row>
              ))}
              {preview.contacts.map(address => (
                <Row key={address}>
                  <Name>{address}</Name>
                  <RolePill>Building contact</RolePill>
                </Row>
              ))}
              {toSender && preview.sender ? (
                <Row key="sender">
                  <Name>{preview.sender.email}</Name>
                  <RolePill>You</RolePill>
                </Row>
              ) : null}
            </List>
          )}

          {toSender && <Muted data-report-to-sender="">{TO_SENDER_NOTE}</Muted>}

          {/* R-23: the sender may not edit this. Said out loud, because a
              read-only list with no explanation reads as a control that is
              broken rather than one that is deliberate. */}
          {!nobody && !toSender && (
            <Muted>
              Recipients are set by the building&apos;s staffing and cannot be
              changed here — a daily report always goes to the people
              responsible for the building.
            </Muted>
          )}
        </>
      ) : null}
    </FormModal>
  );
};

const Lead = styled.p`
  margin: 0 0 ${spacing.md};
  color: ${palette.ink};
  max-width: 60ch;
`;

const SectionTitle = styled.h3`
  margin: 0 0 ${spacing.sm};
  font-size: ${typography.size.sm}px;
  font-weight: ${typography.weight.semibold};
  letter-spacing: ${typography.tracking.wide};
  text-transform: uppercase;
  color: ${palette.slateLight};
`;

const List = styled.ul`
  margin: 0 0 ${spacing.md};
  padding: 0;
  list-style: none;
  border: 1px solid ${palette.line};
  border-radius: ${radius.sm};
  overflow: hidden;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${spacing.sm};
  padding: ${spacing.sm};
  border-bottom: 1px solid ${palette.line};

  &:last-child {
    border-bottom: none;
  }
`;

const Name = styled.span`
  color: ${palette.ink};
  overflow-wrap: anywhere;
`;

const RolePill = styled.span`
  flex: 0 0 auto;
  padding: ${spacing.half} ${spacing.xs};
  border-radius: ${radius.pill};
  background: ${palette.surfaceMuted};
  color: ${palette.slate};
  font-size: ${typography.size.xxs}px;
  font-weight: ${typography.weight.semibold};
  white-space: nowrap;
`;

const Muted = styled.p`
  margin: 0;
  color: ${palette.slate};
  font-size: ${typography.size.xs}px;
  max-width: 60ch;
`;

const Alert = styled.div`
  margin: 0 0 ${spacing.md};
  padding: ${spacing.sm};
  border-radius: ${radius.sm};
  background: ${status.warning.bg};
  color: ${status.warning.fg};
  max-width: 60ch;
`;

const RetryButton = styled.button`
  background: none;
  border: none;
  padding: 0;
  color: inherit;
  font: inherit;
  font-weight: ${typography.weight.semibold};
  text-decoration: underline;
  cursor: pointer;
`;
